"use client";

import { showOnlineExclusiveFeatureFlag } from "@utils/default";
import { AnimatePresence, motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import React, { FC, useContext, useState } from "react";

import AddToFavorites from "@/components/AddToFavorites/AddToFavorites";
import Pagination from "@/components/AvailableUnitsList/Pagination";
import UnitDetails from "@/components/AvailableUnitsList/UnitDetails";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import ModalContent from "@/components/ui/Modal/ModalContent";
import { AvailableUnitContext } from "@/context/AvailableUnitListContext";
import useMediaQuery from "@/hooks/useMediaQuery";
import { CommercetoolsUnit } from "@/types";

import FormattedPrice from "./FormattedPrice";
import MobileFilters from "./MobileFilters";

const Modal = dynamic(() => import("@/components/ui/Modal/Modal"), { ssr: false });
interface Props {
  isBelongToCommunity?: boolean | void;
  matchedCommunity?: any;
}
const UnitsTable: FC<Props> = ({ isBelongToCommunity, matchedCommunity }) => {
  const t = useTranslations();
  const [selectedUnit, setSelectedUnit] = useState<CommercetoolsUnit | null>();
  const { filteredResults } = useContext(AvailableUnitContext);
  const formatUnitName = (name: string): string => name.replaceAll("/", "-") || name;
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [tourModalOpen, setTourModalOpen] = useState<boolean>(false);
  const locale = useLocale();
  const [language, _] = locale.split("-");

  return (
    <>
      {filteredResults.length >= 1 && (
        <>
          <MobileFilters />
          {!isDesktop && filteredResults.length >= 1 && (
            <p className="text-center my-xs md:my-xxl font-secondary text-s font-medium">{t("aul.limited.supply")}</p>
          )}
        </>
      )}
      {filteredResults.length >= 1 && (
        <div className="md:hidden">
          <div className="grid units-grid border-b border-b-gray-10 mb-m gap-xs px-m py-m">
            <div className={`font-secondary font-medium text-s uppercase ${language === "ar" ? "mr-[40px]" : "ml-[10px]"}`}>{t("aul.unitID")}</div>
            <div className={`font-secondary font-medium text-s uppercase ${language === "ar" ? "mr-[40px]" : ""}`}>{t("aul.unitType")}</div>
            <div className={`font-secondary font-medium text-s uppercase ${language === "ar" ? "mr-[40px]" : ""}`}>{t("aul.price")}</div>
            <div className={`font-secondary font-medium text-s uppercase ${language === "ar" ? "mr-[40px]" : ""}`}>{t("aul.actions")}</div>
          </div>
          <AnimatePresence>
            {filteredResults?.map((unit, index: number) => {
              return (
                <motion.button
                  key={unit.id}
                  initial={{ opacity: 0, scaleY: 0 }}
                  animate={{ opacity: 1, scaleY: 1 }}
                  exit={{ opacity: 0, scaleY: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                  onClick={() => setSelectedUnit(unit)}
                  className="origin-top-left grid w-full group units-grid items-center text-left gap-xs transition-colors rounded-full bg-gray-7 h-4xl px-m md:rounded-lg md:even:bg-white md:odd:bg-gray-7 md:hover:box-material md:hover:bg-gray-10 mb-xs"
                  id={`mobile-button-${unit.id}`}
                  unit-id={unit.id}
                  unit-name={formatUnitName(unit.name)}
                >
                  {/* <div className="font-secondary text-s">{unit.name}</div> */}
                  <div className="font-secondary text-s gap-xs w-full">
                    {unit?.exclusive && showOnlineExclusiveFeatureFlag && (
                      <span
                        key={`tag-${index}`}
                        className="box-material-primary backdrop-blur-xl rounded font-normal uppercase py-3xs px-[6px] text-s text-white before:rounded flex-[0.4] text-center bg-gray-2"
                      >
                        {t("filter.label.onlineExclusive")}
                      </span>
                    )}
                    <div>{unit.name}</div>
                  </div>
                  <div className="font-secondary text-s" style={{ marginLeft: "20px" }}>
                    {unit.unitType}
                  </div>
                  <div className="font-secondary text-[11px]">
                    <FormattedPrice price={unit.price} />
                  </div>

                  <div>
                    {unit?.virtualTourUrl && (
                      <div>
                        <Button
                          onClick={() => setTourModalOpen(true)}
                          rounded
                          size={ButtonSize.SMALL}
                          theme={ButtonTheme.WHITE}
                          type="button"
                          variant={ButtonVariant.PRIMARY}
                          customClass="mr-m"
                        >
                          <Icon name="3d" title="3D" size="16" className="stroke-black" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Icon name="arrow-right" className="stroke-black" size="24" />
                  </div>
                  {unit?.virtualTourUrl && (
                    <Modal
                      open={tourModalOpen}
                      onClose={() => {
                        setTourModalOpen(false);
                      }}
                    >
                      <ModalContent className="w-full h-full !rounded-none  py-l">
                        <div className="flex justify-between pb-l">
                          <h2 className="font-medium uppercase font-primary text-m ">{t("unit.3dtourmodal")}</h2>
                          <Button
                            className="modal__close"
                            size={ButtonSize.MEDIUM}
                            variant={ButtonVariant.TERTIARY}
                            theme={ButtonTheme.BLACK}
                            onClick={() => setTourModalOpen(false)}
                          >
                            <span className="pointer-events-none">
                              <Icon name="close" size="24" title="close" className="stroke-black" />
                              <span className="sr-only">{t("button.label.close")}</span>
                            </span>
                          </Button>
                        </div>
                        <iframe
                          className="object-cover w-full h-[calc(100dvh-100px)] rounded-lg border-0"
                          width="560"
                          height="315"
                          src={unit?.virtualTourUrl}
                          title="3d Tour"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                        ></iframe>
                      </ModalContent>
                    </Modal>
                  )}
                </motion.button>
              );
            })}
          </AnimatePresence>
          <Pagination />
        </div>
      )}
      {selectedUnit && (
        <Modal
          open={!!selectedUnit}
          onClose={() => {
            setSelectedUnit(null);
          }}
        >
          <ModalContent className="h-svh !p-0 w-full">
            <div className="h-dvh flex flex-col">
              <header className="flex justify-between p-m border-b border-b-gray-10 md:hidden">
                <h3>{t("aul.unitDetails")}</h3>
                <div className="flex items-center gap-m">
                  {/* <button className="">
                    <Icon
                      className="stroke-black"
                      name="heart-empty"
                      size="24"
                      title={t("button.label.addToFavourite")}
                      fill={false ? "fill-black" : ""}
                    />
                  </button> */}
                  <AddToFavorites item={selectedUnit} className="flex" fillColor="fill-black" />
                  <button
                    onClick={() => {
                      setSelectedUnit(null);
                    }}
                    className="font-secondary text-s underline"
                  >
                    <Icon className="stroke-black" name="close" size="24" title={t("button.label.close")} fill={false ? "fill-black" : ""} />
                  </button>
                </div>
              </header>
              <UnitDetails
                closeHandler={() => {
                  setSelectedUnit(null);
                }}
                unit={selectedUnit!}
                customClass="pt-l grow relative flex flex-col grow"
                isBelongToCommunity={isBelongToCommunity}
                matchedCommunity={matchedCommunity}
              />
            </div>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};
export default UnitsTable;
